import React from 'react';

import isEqual from 'lodash/isEqual';
import isFunction from 'lodash/isFunction';

import { useProcessAsync } from '@gogame/hooks-ts';

import { useScreenMessage } from '../Message';
import { useStorage } from '../Storage';
import ScreenContext from './ScreenContext';
import ScreenWidgetCart from './components/ScreenWidgetCart';
import { useEditPreset } from './hooks';

const REINIT = 'REINIT';

const renderChildren = (children, screenState) => {
  return isFunction(children)
    ? children({ screenState })
    : React.isValidElement(children) &&
        React.cloneElement(React.Children.only(children), {
          screenState,
        });
};

const ScreenWidget = ({
  children,
  title,
  subTitle,
  name,
  reducer,
  description,
  filters,
  actions,
  onClose,
  globalPeriod,
  onCollapse,
  editable,
  collapsed,
  affix,
  active,
}) => {
  const { showSuccessMessage } = useScreenMessage();
  const [loading, processAsync] = useProcessAsync();
  const { state: initialState, save } = useStorage();
  const editPreset = useEditPreset(processAsync);

  const reducerCallback = React.useCallback(
    (state, action) => {
      switch (action.type) {
        case REINIT:
          return action.payload;

        default:
          if (reducer) {
            return reducer(state, action);
          }
          return state;
      }
    },
    [reducer]
  );

  const [screenState, dispatch] = React.useReducer(
    reducerCallback,
    initialState
  );

  React.useEffect(() => {
    if (!isEqual(initialState, screenState)) {
      dispatch({
        type: REINIT,
        payload: initialState,
      });
    }
  }, [initialState]);

  // TODO: move reset to storage
  const handleReset = React.useCallback(() => {
    dispatch({
      type: REINIT,
      payload: initialState,
    });
    showSuccessMessage(`«${screenState.title}» preset changes discarded`);
  }, [initialState]);

  const handleSave = React.useCallback(async () => {
    editPreset(screenState.title, save(screenState));
  }, [name, save, screenState, editPreset]);

  const changed = React.useMemo(
    () => !isEqual(initialState, screenState),
    [initialState, screenState]
  );

  return (
    <ScreenContext.Provider value={{ name, screenState, dispatch }}>
      <ScreenWidgetCart
        onSave={handleSave}
        onReset={handleReset}
        changed={changed}
        loading={loading}
        title={title}
        subTitle={subTitle}
        description={description}
        filters={filters}
        actions={actions}
        onClose={onClose}
        globalPeriod={globalPeriod}
        onCollapse={onCollapse}
        editable={editable}
        collapsed={collapsed}
        affix={affix}
      >
        {active && renderChildren(children, screenState)}
      </ScreenWidgetCart>
    </ScreenContext.Provider>
  );
};

export default ScreenWidget;
